'use client';

/**
 * BL-819: V2 buy box color swatches. Square tiles wrapping at 5 per row,
 * unlike V1's VariantSwatches in this folder (circles, labelled, 3 per row).
 */

import { useMemo } from 'react';
import { useProduct } from '@/contexts/ProductContext';
import { getSelectedOptionValue } from '@/utils/product/product-context-helpers';

import {
  BIRCH_COLOR_OPTION_NAME,
  getShopifyOptionByName,
} from '@/components/blocks/variantOptions/shopifyOptionConventions';

// The CMS gives the fill as a `bg-<color>` class and Shopify free text ("Light
// Oak"), so separators never line up: compare letters and digits alone. A new
// color also needs a token and an `@source inline` entry in globals.css, or
// the class paints nothing.
const colorKey = (value) =>
  (value || '').replace(/^bg-/, '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Content mistakes that would otherwise drop a swatch, or the block, in silence.
const warnInDev = (message) => {
  if (process.env.NODE_ENV === 'development') console.warn(`[V2ColorSwatches] ${message}.`);
};

const SWATCH_TILE =
  'box-border size-14 cursor-pointer outline-none transition-colors duration-150 hover:ring-1 hover:ring-inset hover:ring-sand has-focus-visible:ring-2 has-focus-visible:ring-sea has-focus-visible:ring-offset-2';

const V2ColorSwatches = ({ block }) => {
  const { selectedVariant, updateOptions, productData } = useProduct();

  // `section_label` is the display heading only; renaming it in the CMS must
  // not break the lookup, which always uses BIRCH_COLOR_OPTION_NAME.
  const { section_label, swatches: swatchRows = [] } = block ?? {};
  const heading = (section_label || '').trim() || BIRCH_COLOR_OPTION_NAME;

  const colorOption = getShopifyOptionByName(productData?.shopifyProduct, BIRCH_COLOR_OPTION_NAME);
  const currentValue = getSelectedOptionValue(selectedVariant, BIRCH_COLOR_OPTION_NAME);

  // Skip rows Shopify has no value for (e.g. a removed color) and rows
  // repeating one, which would render as two children with the same key.
  const renderedSwatches = useMemo(() => {
    if (!colorOption) {
      warnInDev(`Shopify has no "${BIRCH_COLOR_OPTION_NAME}" option on this product`);
      return [];
    }

    return swatchRows.reduce((acc, { colors }) => {
      const optionValue = colorOption.values.find((v) => colorKey(v) === colorKey(colors));

      if (!optionValue) {
        warnInDev(`"${colors}" matches no ${BIRCH_COLOR_OPTION_NAME} value in Shopify`);
      } else if (acc.some((s) => s.optionValue === optionValue)) {
        warnInDev(`"${colors}" is listed more than once`);
      } else {
        acc.push({ colors, optionValue });
      }
      return acc;
    }, []);
  }, [colorOption, swatchRows]);

  if (!renderedSwatches.length || !selectedVariant) return null;

  return (
    <section className="flex flex-col gap-4 md:my-4 font-worksans text-primary" data-block="v2_color_swatches">
      <p className="flex items-center gap-2 text-xl font-medium leading-5">
        <span>{heading}</span>
        {currentValue ? <span className="font-normal">{currentValue}</span> : null}
      </p>

      <fieldset className="border-0 p-0">
        <legend className="sr-only">{heading}</legend>
        <div className="flex max-w-78 flex-wrap gap-2">
          {renderedSwatches.map(({ colors, optionValue }) => {
            const isSelected = currentValue === optionValue;

            return (
              <label
                key={optionValue}
                title={optionValue}
                className={`${SWATCH_TILE} ${colors} ${isSelected ? 'border-[2.5px] border-sea' : 'border border-sand'}`}
              >
                <input
                  type="radio"
                  name={`variant-${BIRCH_COLOR_OPTION_NAME}`}
                  value={optionValue}
                  checked={isSelected}
                  aria-label={optionValue}
                  className="sr-only"
                  onChange={() =>
                    updateOptions([{ name: BIRCH_COLOR_OPTION_NAME, value: optionValue }], { persist: true })
                  }
                />
              </label>
            );
          })}
        </div>
      </fieldset>
    </section>
  );
};

export default V2ColorSwatches;
